import React, { useState, useEffect } from "react";
import { Button, Card, Col, Container, Form, Row, Alert, Spinner } from "react-bootstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { getAuth, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { Formik, Form as FormikForm, Field } from 'formik';
import * as Yup from 'yup';
import pageSvg from "../assets/svg/reset_password.svg";
import logo from "../assets/img/brandimg.png";

// Validation schema
const ResetPasswordSchema = Yup.object().shape({
    password: Yup.string()
        .min(8, 'Password must be at least 8 characters')
        .required('Password is required'),
    confirmPassword: Yup.string()
        .oneOf([Yup.ref('password'), null], 'Passwords must match')
        .required('Confirm password is required'),
});

export default function ResetPassword() {
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [validCode, setValidCode] = useState(false);
    const [verifying, setVerifying] = useState(true);
    
    const navigate = useNavigate();
    const location = useLocation();
    const auth = getAuth();

    // Get oobCode from URL parameters
    const queryParams = new URLSearchParams(location.search);
    const oobCode = queryParams.get('oobCode');

    useEffect(() => {
        const verifyCode = async () => {
            if (!oobCode) {
                setError('Invalid password reset link');
                setVerifying(false);
                return;
            }

            try {
                await verifyPasswordResetCode(auth, oobCode);
                setValidCode(true);
            } catch (error) {
                console.error('Error verifying reset code:', error);
                switch (error.code) {
                    case 'auth/expired-action-code':
                        setError('This password reset link has expired. Please request a new one.');
                        break;
                    case 'auth/invalid-action-code':
                        setError('Invalid password reset link. Please request a new one.');
                        break;
                    default:
                        setError('Invalid or expired password reset link');
                }
            } finally {
                setVerifying(false);
            }
        };

        verifyCode();
    }, [auth, oobCode]);

    if (verifying) {
        return (
            <div className="page-auth">
                <Container>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
                        <Spinner animation="border" role="status">
                            <span className="visually-hidden">Verifying reset link...</span>
                        </Spinner>
                    </div>
                </Container>
            </div>
        );
    }

    return (
        <div className="page-auth">
            <div className="header">
                <Container>
                    <img src={logo} alt="logo" className="header-logo" style={{ width: '170px', height: '170px' }}/>
                </Container>
            </div>

            <div className="content">
                <Container>
                    <Card className="card-auth">
                        <Card.Body className="text-center">
                            <div className="mb-5">
                                <object type="image/svg+xml" data={pageSvg} className="w-50" aria-label="svg image"></object>
                            </div>
                            <Card.Title>Reset your password</Card.Title>
                            <Card.Text className="mb-4">Please enter your new password below.</Card.Text>

                            {error && <Alert variant="danger">{error}</Alert>}
                            {success && <Alert variant="success">{success}</Alert>}

                            <Formik
                                initialValues={{
                                    password: '',
                                    confirmPassword: ''
                                }}
                                validationSchema={ResetPasswordSchema}
                                onSubmit={async (values, { setSubmitting }) => {
                                    if (!validCode) {
                                        setError('Invalid reset link');
                                        setSubmitting(false);
                                        return;
                                    }

                                    try {
                                        setError('');
                                        await confirmPasswordReset(auth, oobCode, values.password);
                                        
                                        setSuccess('Password has been reset successfully! Redirecting to login...');
                                        setTimeout(() => {
                                            navigate('/auth/signin');
                                        }, 3000);
                                    } catch (error) {
                                        console.error('Error resetting password:', error);
                                        switch (error.code) {
                                            case 'auth/weak-password':
                                                setError('Password is too weak. Please choose a stronger password.');
                                                break;
                                            default:
                                                setError('Failed to reset password. Please try again.');
                                        }
                                    } finally {
                                        setSubmitting(false);
                                    }
                                }}
                            >
                                {({ errors, touched, isSubmitting }) => (
                                    <FormikForm noValidate>
                                <Row className="g-2">
                                    <Col sm="12" className="mb-3">
                                                <Form.Group>
                                                    <Field
                                            type="password"
                                                        name="password"
                                            placeholder="New password"
                                                        className={`form-control ${touched.password && errors.password ? 'is-invalid' : ''}`}
                                                        disabled={isSubmitting || !validCode}
                                                    />
                                                    {touched.password && errors.password && (
                                                        <Form.Control.Feedback type="invalid" className="d-block">
                                                            {errors.password}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                    </Col>
                                    <Col sm="12" className="mb-3">
                                                <Form.Group>
                                                    <Field
                                            type="password"
                                                        name="confirmPassword"
                                            placeholder="Confirm new password"
                                                        className={`form-control ${touched.confirmPassword && errors.confirmPassword ? 'is-invalid' : ''}`}
                                                        disabled={isSubmitting || !validCode}
                                                    />
                                                    {touched.confirmPassword && errors.confirmPassword && (
                                                        <Form.Control.Feedback type="invalid" className="d-block">
                                                            {errors.confirmPassword}
                                                        </Form.Control.Feedback>
                                                    )}
                                                </Form.Group>
                                    </Col>
                                    <Col sm="12">
                                        <Button 
                                            variant="primary" 
                                            type="submit"
                                                    disabled={isSubmitting || !validCode}
                                            className="w-100"
                                        >
                                                    {isSubmitting ? (
                                                <>
                                                    <Spinner
                                                        as="span"
                                                        animation="border"
                                                        size="sm"
                                                        role="status"
                                                        aria-hidden="true"
                                                        className="me-2"
                                                    />
                                                    Resetting...
                                                </>
                                                    ) : 'Reset Password'}
                                        </Button>
                                    </Col>
                                </Row>
                                    </FormikForm>
                                )}
                            </Formik>
                        </Card.Body>
                    </Card>
                </Container>
            </div>
        </div>
    );
} 